import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getIndividualList, getIndividualSumary } from '../../../api/statements';
import { Layout, Loader, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import {
  fetchDashboardFilterData,
  individualSummaryTableColumns,
  individualTableColumns,
  reports,
} from '../../../utils/constant';
import { formatDate, getPrice } from '../../../utils/formatters';
import { getModifiedIndividualData, makeDownloadReady } from '../../../utils/utils';
import NewCommonFilters from '../../dashboard/newCommonFilters';
import DownloadModal from '../../reports/downloadModal/downloadModal';
import EmailModal from '../../reports/emailModal/emailModal';
import ItemEmptyTile from '../../reports/item-empty-tile/item-empty-tile';
import Toolbar from '../../reports/toolbar/toolbar';
import CommonFilter from '../commonFilter';
import PreviewPDF from '../previewPDF';
import IndividualStatementRow from '../statementRow';

const IndividualStatements = () => {
  const [data, setData] = useState([]);
  const [nextData, setNextData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [_addendumInProgress, setAddendumInProgress] = useState(false);
  const [addendumNotes, setAddendumNotes] = useState(null);
  const [apdNote, setAPDNote] = useState(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [payload, setPayload] = useState({});
  const [downloadDetails, setDownloadDetails] = useState({});
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [initialRenderDone, setInitialRenderDone] = useState(false);
  const [totalDownpayment, setTotalDownpayment] = useState(null);
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);

  useEffect(() => {
    if (initialRenderDone && payload?.startDate && payload?.endDate) {
      generateReport(payload);
    }
    setInitialRenderDone(true);
  }, [filterData]);

  const hidePreview = () => {
    setDownloadDetails({ ...downloadDetails, showPreviewModal: false });
  };
  const downloadFile = () => {
    makeDownloadReady(
      downloadDetails.response,
      downloadDetails.exportType,
      downloadDetails.reportType,
    );
    setDownloadDetails({ ...downloadDetails, showPreviewModal: false });
  };
  const toggleEmailModal = () => {
    if (data?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const toggleDownloadModal = () => {
    if (data?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };

  const generateReport = async (payloadData) => {
    if (!payloadData.opiOrName || !payloadData.startDate || !payloadData.startDate) {
      return;
    }
    setLoading(true);
    try {
      setPayload({
        ...payloadData,
        ...fetchDashboardFilterData(filterData),
      });
      const response = await getIndividualList({
        ...payloadData,
        ...fetchDashboardFilterData(filterData),
      });
      setShowTable(true);
      const individualData = getModifiedIndividualData(response?.data?.data);
      setTotalDownpayment(response?.data?.totalDownpayment);
      setData(individualData);
      setAddendumNotes(response?.data?.addendumNotes);
      setAPDNote(response?.data?.financeWithAPDNote);
      setLoading(false);
    } catch (_err) {
      setLoading(false);
    }
    try {
      const summaryResponse = await getIndividualSumary({
        ...payloadData,
        ...fetchDashboardFilterData(filterData),
      });
      setSummary(summaryResponse?.data?.data?.summaryReport[0]);
      setNextData(summaryResponse?.data?.data?.nextReport);
      setAddendumInProgress(summaryResponse?.data?.data?.isAddendumInProgress);
    } catch (err) {
      console.log(err);
    }
  };
  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const renderSummaryTable = () => {
    return (
      <Table headers={individualSummaryTableColumns} bordered responsive className="simple-table">
        <tbody>
          <tr className="table-body-tr">
            <td>{getPrice(summary?.current)}</td>
            <td>{getPrice(summary?.day30) || '-'}</td>
            <td>{getPrice(summary?.day60) || '-'}</td>
            <td>{getPrice(summary?.day90) || '-'}</td>
            <td>{getPrice(summary?.day120) || '-'}</td>
            <td>{getPrice(summary?.above120) || '-'}</td>
          </tr>
        </tbody>
      </Table>
    );
  };

  const renderBottomDetails = () => {
    return (
      <div className="bottomDiv">
        <div className="nextPayment">
          {nextData?.paymentDueUponReceipt ? (
            <span className="nextPayment-text1">Payment due upon Receipt</span>
          ) : (
            <div>
              <span className="nextPayment-text1">
                Next Payment due : {formatDate(nextData?.expectedPaymentDate)}
              </span>
              <span className="nextPayment-text1-amount">
                {getPrice(nextData?.expectedPaymentAmount)}
              </span>
            </div>
          )}
        </div>
        {(addendumNotes || apdNote) && (
          <div className="noteDiv">
            {apdNote && <p className="addendumText">Note: {apdNote}</p>}
            {addendumNotes && <p className="addendumText">Note: {addendumNotes}</p>}
            <hr />
          </div>
        )}
        <div className="bottomDetail">
          <span>
            Please Return this portion with your payment and reference your
            <br />
            account number on your check or money order
          </span>
        </div>
      </div>
    );
  };
  const renderModals = () => {
    return (
      <>
        <EmailModal
          isOpen={isEmailModalOpen}
          toggle={toggleEmailModal}
          reportType={reports.INDIVIDUAL}
          apiValues={payload}
          newFilters
        />
        <DownloadModal
          isOpen={isDownloadModalOpen}
          toggle={toggleDownloadModal}
          reportType={reports.INDIVIDUAL}
          urlParams={payload}
          showDownloadPreview
          onDownloadClick={onDownloadClick}
          newFilters
        />
      </>
    );
  };

  const onDownloadClick = (response, exportType, reportType) => {
    setDownloadDetails({
      response,
      exportType,
      showPreviewModal: true,
      reportType,
    });
  };
  return (
    <>
      <Layout>
        <div className="layout-in-div">
          <NewCommonFilters />
          <CommonFilter
            title="Individual Customer Statement"
            generateReport={generateReport}
            type="individual"
            searchText={searchText}
            fromDate={fromDate}
            toDate={toDate}
            setSearchText={setSearchText}
            setFromDate={setFromDate}
            setToDate={setToDate}
          />
          {showTable && (
            <>
              <Toolbar
                optionOneLabel="Email Report"
                optionOneIcon="MdOutlineEmail"
                onOptionOneClick={toggleEmailModal}
                optionTwoLabel="Download Report"
                optionTwoIcon="FiDownload"
                onOptionTwoClick={toggleDownloadModal}
                isTableDataPresent={data?.length}
              />
              {data && data.length > 0 ? (
                <>
                  <Table
                    headers={individualTableColumns}
                    bordered
                    responsive
                    className="dashboard-table"
                  >
                    <tbody>
                      {data?.map((rowData, rowNumber) => (
                        <IndividualStatementRow
                          key={rowNumber}
                          rowData={rowData}
                          id={rowNumber + 1}
                        />
                      ))}
                    </tbody>
                  </Table>
                  <div className="nextPayment">
                    <span className="nextPayment-text1">
                      Note: Total Down Payment {getPrice(totalDownpayment)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="loader-empty">
                  <LoaderComponent />
                </div>
              )}
            </>
          )}
          {data && data.length > 0 && summary && renderSummaryTable()}
          {data && data.length > 0 && nextData && renderBottomDetails()}
        </div>
      </Layout>
      {downloadDetails?.showPreviewModal && (
        <PreviewPDF
          onCancel={hidePreview}
          onDownload={downloadFile}
          file={downloadDetails?.response}
        />
      )}
      {renderModals()}
    </>
  );
};

export default IndividualStatements;
